import React, { useEffect, useState } from 'react'
import {
  FormControl as FormItemControl,
  FormLabel as FormItemLabel,
  Grid,
  RadioGroup as ItemGroup,
  Stack,
} from '@mui/material'
import { Path, useFormContext } from 'react-hook-form'
import FormInput from 'components/FormInput'
import { Box } from '@mui/system'
import Label from 'components/Label'
import FormControlRadio from 'components/FormControlRadio'
import FormCheckbox from 'components/FormControlCheckbox'
import { substances } from 'types/hearing-identification.type'
import { FormInputSelect } from 'components/FormInputSelect'
import { ItemOption } from 'components/InputSelect'
import { SocioeducationAccompanimentDataType } from 'types/socioeducation-accompaniment-data.type'
import { FormInputFile } from 'components/FormInputFile'
import { extractFileExtension } from 'utils/document.util'

export const relacaoAdolescenteDrogaOptions = [
  { label: 'Uso Recreativo', value: 'RECREATIVO' },
  { label: 'Uso Abusivo com prejuizos para as atividades', value: 'ABUSIVO' },
]

export const acompSaudeMentalOptions: ItemOption[] = [
  { value: 'CAPS', label: 'CAPS' },
  { value: 'CAPSI', label: 'CAPSI' },
  { value: 'CAPSAD', label: 'CAPSAD' },
  { value: 'AMBULATORIO_SAUDE_MENTAL', label: 'Ambulatório de Saúde Mental' },
  { value: 'UAI', label: 'UAI' },
]

const simNaoOptions = [
  {
    label: 'Sim',
    value: 'S',
  },
  {
    label: 'Não',
    value: 'N',
  },
]

interface ComprehensiveHealthProps {
  control: any
  doNotHasEditPermission: boolean
  watch: (name: (value: any) => void) => any
}

const ComprehensiveHealth: React.FC<ComprehensiveHealthProps> = ({
  control,
  doNotHasEditPermission,
  watch,
}) => {
  const { setValue, setError, clearErrors } = useFormContext<SocioeducationAccompanimentDataType>()

  const anexoArquivoBase64Field =
    `meioAbertoAcompInfoSaudeIntegral.anexo.arquivoBase64` as Path<SocioeducationAccompanimentDataType>
  const anexoArquivoTipoField =
    `meioAbertoAcompInfoSaudeIntegral.anexo.arquivoTipo` as Path<SocioeducationAccompanimentDataType>
  const anexoArquivoNomeField =
    `meioAbertoAcompInfoSaudeIntegral.anexo.arquivoNome` as Path<SocioeducationAccompanimentDataType>

  const [isVinculadoUnidadeSaude, setIsVinculadoUnidadeSaude] = useState<boolean | null>(null)
  const [isPossuiDoencaCronica, setIsPossuiDoencaCronica] = useState<boolean | null>(null)
  const [isPossuiDeficiencia, setIsPossuiDeficiencia] = useState<boolean | null>(null)
  const [isAcompSaudeMental, setIsAcompSaudeMental] = useState<boolean | null>(null)
  const [isFazUsoDrogas, setIsFazUsoDrogas] = useState<boolean | null>(null)
  const [isPossuiLaudoDeficiencia, setIsPossuiLaudoDeficiencia] = useState<boolean | null>(null)

  useEffect(() => {
    const subscription = watch((value) => {
      const parseStringToBoolean = (val: string) => {
        if (val === 'S') return true
        if (val === 'N') return false
        return null
      }

      setIsVinculadoUnidadeSaude(
        parseStringToBoolean(value.meioAbertoAcompInfoSaudeIntegral?.vinculadoUnidadeSaude),
      )
      setIsPossuiDoencaCronica(
        parseStringToBoolean(value.meioAbertoAcompInfoSaudeIntegral?.possuiDoencaCronica),
      )
      setIsPossuiDeficiencia(
        parseStringToBoolean(value.meioAbertoAcompInfoSaudeIntegral?.possuiDeficiencia),
      )
      setIsAcompSaudeMental(
        parseStringToBoolean(
          value.meioAbertoAcompInfoSaudeIntegral?.possuiAcompanhamentoSaudeMental,
        ),
      )
      setIsFazUsoDrogas(
        parseStringToBoolean(value.meioAbertoAcompInfoSaudeIntegral?.fazUsoDrogasLicitasIlicitas),
      )
      setIsPossuiLaudoDeficiencia(
        parseStringToBoolean(
          value.meioAbertoAcompInfoSaudeIntegral?.possuiLaudoDeficienciaIntelectual,
        ),
      )
    })
    return () => subscription.unsubscribe()
  }, [watch])

  useEffect(() => {
    if (!isVinculadoUnidadeSaude) {
      setValue('meioAbertoAcompInfoSaudeIntegral.unidadeSaude', null)
    }
  }, [isVinculadoUnidadeSaude])

  useEffect(() => {
    if (!isPossuiDoencaCronica) {
      setValue('meioAbertoAcompInfoSaudeIntegral.doencasCronicas', null)
    }
  }, [isPossuiDoencaCronica])

  useEffect(() => {
    if (!isPossuiDeficiencia) {
      setValue('meioAbertoAcompInfoSaudeIntegral.deficiencia', null)
    }
  }, [isPossuiDeficiencia])

  useEffect(() => {
    if (!isAcompSaudeMental) {
      setValue('meioAbertoAcompInfoSaudeIntegral.acompanhamentoSaudeMental', null)
    }
  }, [isAcompSaudeMental])

  useEffect(() => {
    if (!isFazUsoDrogas) {
      setValue('meioAbertoAcompInfoSaudeIntegral.substancias.usaBebida', 'N')
      setValue('meioAbertoAcompInfoSaudeIntegral.substancias.usaCigarro', 'N')
      setValue('meioAbertoAcompInfoSaudeIntegral.substancias.usaCocaina', 'N')
      setValue('meioAbertoAcompInfoSaudeIntegral.substancias.usaCrack', 'N')
      setValue('meioAbertoAcompInfoSaudeIntegral.substancias.usaHaxixe', 'N')
      setValue('meioAbertoAcompInfoSaudeIntegral.substancias.usaHeroina', 'N')
      setValue('meioAbertoAcompInfoSaudeIntegral.substancias.usaLolo', 'N')
      setValue('meioAbertoAcompInfoSaudeIntegral.substancias.usaMaconha', 'N')
      setValue('meioAbertoAcompInfoSaudeIntegral.substancias.usaOutros', 'N')
      setValue('meioAbertoAcompInfoSaudeIntegral.substancias.usaSubstancia', 'N')
    }
  }, [isFazUsoDrogas])

  return (
    <Grid container item xs={12} spacing={2}>
      <Grid container item xs={12} spacing={2}>
        <Grid item md={5} xs={12}>
          <FormControlRadio
            control={control}
            name='meioAbertoAcompInfoSaudeIntegral.vinculadoUnidadeSaude'
            label='Adolescente/Jovem chegou ao serviço vinculado a alguma Unidade de Saúde?'
            options={simNaoOptions}
            disabled={doNotHasEditPermission}
          />
        </Grid>
        {isVinculadoUnidadeSaude && (
          <>
            <Grid item md={7} xs={12}>
              <FormInput
                name='meioAbertoAcompInfoSaudeIntegral.unidadeSaude'
                label='Se sim, qual?'
                control={control}
                disabled={doNotHasEditPermission}
              />
            </Grid>
          </>
        )}
        {isVinculadoUnidadeSaude != null && !isVinculadoUnidadeSaude && (
          <>
            <Grid item md={7} xs={12}>
              <FormInput
                name='meioAbertoAcompInfoSaudeIntegral.clinicaDeFamiliaDeReferencia'
                label='Caso não, qual Clínica da Família de referência para atendimento pelo PNAISARI?'
                control={control}
                disabled={doNotHasEditPermission}
              />
            </Grid>
          </>
        )}
      </Grid>
      <Grid container item xs={12} spacing={2}>
        <Grid item md={12} xs={12}>
          <FormControlRadio
            control={control}
            name='meioAbertoAcompInfoSaudeIntegral.possuiPlanoParticular'
            label='Possui Plano de Saúde Particular?'
            options={simNaoOptions}
            disabled={doNotHasEditPermission}
          />
        </Grid>
      </Grid>
      <Grid container item xs={12} spacing={2}>
        <Grid item md={12} xs={12}>
          <FormControlRadio
            control={control}
            name='meioAbertoAcompInfoSaudeIntegral.projetoTerapeuticoSingularOuSaudeCompUnSaude'
            label='Projeto Terapêutico Singular ou informações de saúde compartilhadas pela Unidade de Referência?'
            options={simNaoOptions}
            disabled={doNotHasEditPermission}
          />
        </Grid>
      </Grid>
      <Grid container item xs={12} spacing={2}>
        <Grid item md={3} xs={12}>
          <FormControlRadio
            control={control}
            name='meioAbertoAcompInfoSaudeIntegral.possuiDoencaCronica'
            label='Possui alguma doença crônica?'
            options={simNaoOptions}
            disabled={doNotHasEditPermission}
          />
        </Grid>
        {isPossuiDoencaCronica && (
          <>
            <Grid item md={9} xs={12}>
              <FormInput
                name='meioAbertoAcompInfoSaudeIntegral.doencasCronicas'
                label='Se sim, qual?'
                control={control}
                disabled={doNotHasEditPermission}
              />
            </Grid>
          </>
        )}
      </Grid>
      <Grid container item xs={12} spacing={2}>
        <Grid item md={3} xs={12}>
          <FormControlRadio
            control={control}
            name='meioAbertoAcompInfoSaudeIntegral.possuiDeficiencia'
            label='Possui deficiência?'
            options={simNaoOptions}
            disabled={doNotHasEditPermission}
          />
        </Grid>
        {isPossuiDeficiencia && (
          <>
            <Grid item md={9} xs={12}>
              <FormInput
                name='meioAbertoAcompInfoSaudeIntegral.deficiencia'
                label='Se sim, qual?'
                control={control}
                disabled={doNotHasEditPermission}
              />
            </Grid>
          </>
        )}
      </Grid>
      <Grid container item xs={12} spacing={2}>
        <Grid item md={3} xs={12}>
          <FormControlRadio
            control={control}
            name='meioAbertoAcompInfoSaudeIntegral.possuiAcompanhamentoSaudeMental'
            label='Acompanhamento em Saúde Mental?'
            options={simNaoOptions}
            disabled={doNotHasEditPermission}
          />
        </Grid>
        {isAcompSaudeMental && (
          <>
            <Grid item md={4} xs={12}>
              <FormInputSelect
                control={control}
                name='meioAbertoAcompInfoSaudeIntegral.acompanhamentoSaudeMental'
                label='Se sim, qual?'
                options={acompSaudeMentalOptions}
              />
            </Grid>
            <Grid item md={5} xs={12}>
              <FormInput
                name='meioAbertoAcompInfoSaudeIntegral.unidadeSaudeMental'
                label='Qual Unidade?'
                control={control}
                disabled={doNotHasEditPermission}
              />
            </Grid>
          </>
        )}
      </Grid>
      <Grid container item xs={12} spacing={2}>
        <Grid item md={3} xs={12}>
          <FormControlRadio
            control={control}
            name='meioAbertoAcompInfoSaudeIntegral.fazUsoDrogasLicitasIlicitas'
            label='Faz uso de Drogas Licitas e Ilicitas?'
            options={simNaoOptions}
            disabled={doNotHasEditPermission}
          />
        </Grid>
        {isFazUsoDrogas && (
          <>
            <Grid item xs={12}>
              <Stack direction='row'>
                <FormItemControl required={isFazUsoDrogas}>
                  <FormItemLabel
                    id='substances-options-id'
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                  >
                    <Label text='Se sim, quais?' sx={{ fontSize: 14, color: 'text.primary' }} />
                  </FormItemLabel>
                  <ItemGroup row>
                    {substances.map((item, i) => (
                      <FormCheckbox
                        key={i}
                        control={control}
                        setValue={setValue}
                        name={`meioAbertoAcompInfoSaudeIntegral.substancias.${item.name}`}
                        label={item.label}
                      />
                    ))}
                  </ItemGroup>
                </FormItemControl>
              </Stack>
            </Grid>
            <Grid item md={12} xs={12}>
              <FormControlRadio
                control={control}
                name='meioAbertoAcompInfoSaudeIntegral.relacaoAdolescenteDroga'
                setValue={setValue}
                label='Qual a relação o adolescente ou jovem considera ter com as drogas?'
                options={relacaoAdolescenteDrogaOptions}
                disabled={doNotHasEditPermission}
              />
            </Grid>
            <Grid item md={12} xs={12}>
              <Box display={'flex'} flexDirection={'column'} gap={1}>
                <Label
                  text={
                    'Como os responsáveis acompanham e avaliam o uso de droga pelo adolescente/jovem'
                  }
                  sx={{ fontSize: 14, color: 'text.primary' }}
                />
                <FormInput
                  name='meioAbertoAcompInfoSaudeIntegral.avaliacaoResponsaveisUsoDroga'
                  control={control}
                  multiline
                  minRows={4}
                  disabled={doNotHasEditPermission}
                />
              </Box>
            </Grid>
          </>
        )}
      </Grid>
      <Grid container item xs={12} spacing={2}>
        <Grid item md={5} xs={12}>
          <FormControlRadio
            control={control}
            name='meioAbertoAcompInfoSaudeIntegral.possuiLaudoDeficienciaIntelectual'
            label='Possui laudo médico de transtorno mental ou deficiência intelectual?'
            options={simNaoOptions}
            disabled={doNotHasEditPermission}
          />
        </Grid>
        <Grid item md={6} xs={12}>
          <FormInputFile
            control={control}
            name={anexoArquivoBase64Field}
            nameFilename={anexoArquivoTipoField}
            nameFiletype={anexoArquivoNomeField}
            setValue={setValue}
            setError={setError}
            clearErrors={clearErrors}
            disabled={!isPossuiLaudoDeficiencia}
            onValidDocumentFile={(file) => {
              const acceptedFormats = ['pdf']
              const maxFileSizeInBytes = 10 * 1024 * 1024 // 10MB

              const fileExtension = extractFileExtension(file.name)
              if (!fileExtension || !acceptedFormats.includes(fileExtension.toLocaleLowerCase())) {
                return false
              }

              return file.size <= maxFileSizeInBytes
            }}
          />
        </Grid>
      </Grid>
      <Grid container item xs={12} spacing={2}>
        <Grid item md={5} xs={12}>
          <FormControlRadio
            control={control}
            name='meioAbertoAcompInfoSaudeIntegral.revisaoMedicaTranstornoOuDeficienciaIntelectual'
            label='Transtorno mental ou deficiência intelectual que enseje a revisão de medida aplicada?'
            options={simNaoOptions}
            disabled={doNotHasEditPermission}
          />
        </Grid>
      </Grid>
      <Grid container item xs={12} spacing={2}>
        <Grid item md={12} xs={12}>
          <Box display={'flex'} flexDirection={'column'} gap={1}>
            <Label text={'Observações:'} sx={{ fontSize: 14, color: 'text.primary' }} />
            <FormInput
              name='meioAbertoAcompInfoSaudeIntegral.obsevacoes'
              control={control}
              multiline
              minRows={4}
              disabled={doNotHasEditPermission}
            />
          </Box>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default ComprehensiveHealth
